/**
 * Fleet Store
 * Manages fleet status data with automatic polling
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { servicesContainer } from '@/core/di/services-container';
import {
  FleetSummary,
  ConnectorStatusBreakdown,
  Machine,
  MachineMapPoint,
  SummaryPeriod,
} from '../types/dashboard-types';
import { MachineFilters } from '../types/filter-types';

// Polling intervals (in milliseconds)
const FLEET_SUMMARY_POLL_INTERVAL = 30000;  // 30 seconds
const FLEET_STATUS_POLL_INTERVAL = 60000;   // 60 seconds

interface FleetState {
  // Summary data
  summary: FleetSummary | null;
  connectorBreakdown: ConnectorStatusBreakdown[];
  onlinePercentage: number;
  chargingPercentage: number;

  // Period filtering (NEW)
  selectedPeriod: SummaryPeriod;
  customDateRange?: { from: Date; to: Date };

  // Machine list data
  machines: Machine[];
  totalMachines: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;

  // Filters
  filters: MachineFilters;

  // Map data
  mapPoints: MachineMapPoint[];
  isLoadingMap: boolean;
  mapError: string | null;

  // Loading states
  isLoadingSummary: boolean;
  isLoadingMachines: boolean;
  isRefreshing: boolean;

  // Error states
  summaryError: string | null;
  machinesError: string | null;

  // Polling state
  summaryPollIntervalId: ReturnType<typeof setInterval> | null;
  statusPollIntervalId: ReturnType<typeof setInterval> | null;
  lastSummaryUpdate: Date | null;
  lastStatusUpdate: Date | null;

  // Actions
  loadSummary: () => Promise<void>;
  loadMapPoints: () => Promise<void>;
  loadMachines: () => Promise<void>;
  loadPage: (page: number) => Promise<void>;
  setPageSize: (size: number) => void;
  setFilters: (filters: Partial<MachineFilters>) => void;
  clearFilters: () => void;
  setPeriod: (period: SummaryPeriod) => void;  // NEW
  setCustomDateRange: (from: Date, to: Date) => void;  // NEW
  refreshAll: () => Promise<void>;
  startPolling: () => void;
  stopPolling: () => void;
  reset: () => void;
}

const initialState = {
  summary: null,
  connectorBreakdown: [],
  onlinePercentage: 0,
  chargingPercentage: 0,
  selectedPeriod: '30days' as SummaryPeriod,  // Default to 30 days
  customDateRange: undefined,
  machines: [],
  totalMachines: 0,
  totalPages: 0,
  currentPage: 0,
  pageSize: 20,
  filters: {},
  mapPoints: [] as MachineMapPoint[],
  isLoadingMap: false,
  mapError: null,
  isLoadingSummary: false,
  isLoadingMachines: false,
  isRefreshing: false,
  summaryError: null,
  machinesError: null,
  summaryPollIntervalId: null,
  statusPollIntervalId: null,
  lastSummaryUpdate: null,
  lastStatusUpdate: null,
};

export const useFleetStore = create<FleetState>()(
  persist(
    (set, get) => ({
      ...initialState,

      loadSummary: async () => {
        set({ isLoadingSummary: true, summaryError: null });

        try {
          const services = servicesContainer.getServices();

          // For Live Status: Pass undefined to get real-time current state (not historical)
          const result = await services.getFleetSummaryUseCase.execute(
            undefined,
            undefined
          );

          if (result.hasError) {
            set({
              summaryError: result.errorMessage,
              isLoadingSummary: false,
            });
            return;
          }

          set({
            summary: result.summary,
            connectorBreakdown: result.connectorBreakdown,
            onlinePercentage: result.onlinePercentage,
            chargingPercentage: result.chargingPercentage,
            isLoadingSummary: false,
            lastSummaryUpdate: new Date(),
          });
        } catch (error) {
          set({
            summaryError: error instanceof Error ? error.message : 'Failed to load summary',
            isLoadingSummary: false,
          });
        }
      },

      loadMapPoints: async () => {
        set({ isLoadingMap: true, mapError: null });
        try {
          const services = servicesContainer.getServices();
          const result = await services.getFleetMapUseCase.execute();
          if (result.hasError) {
            set({ mapError: result.errorMessage ?? null, isLoadingMap: false });
            return;
          }
          set({ mapPoints: result.points, isLoadingMap: false });
        } catch (error) {
          set({
            mapError: error instanceof Error ? error.message : 'Failed to load map',
            isLoadingMap: false,
          });
        }
      },

      loadMachines: async () => {
        const { currentPage, pageSize, filters } = get();
        set({ isLoadingMachines: true, machinesError: null });

        try {
          const services = servicesContainer.getServices();
          const result = await services.getFleetStatusUseCase.execute(
            currentPage,
            pageSize,
            filters
          );

          if (result.hasError) {
            set({
              machinesError: result.errorMessage,
              isLoadingMachines: false,
            });
            return;
          }

          set({
            machines: result.machines,
            totalMachines: result.totalCount,
            totalPages: result.totalPages,
            isLoadingMachines: false,
            lastStatusUpdate: new Date(),
          });
        } catch (error) {
          set({
            machinesError: error instanceof Error ? error.message : 'Failed to load machines',
            isLoadingMachines: false,
          });
        }
      },

      loadPage: async (page: number) => {
        set({ currentPage: page });
        await get().loadMachines();
      },

      setPageSize: (size: number) => {
        set({ pageSize: size, currentPage: 0 });
        get().loadMachines();
      },

      setFilters: (newFilters: Partial<MachineFilters>) => {
        const { filters } = get();
        set({
          filters: { ...filters, ...newFilters },
          currentPage: 0,
        });
        get().loadMachines();
      },

      clearFilters: () => {
        set({
          filters: {},
          currentPage: 0,
        });
        get().loadMachines();
      },

      setPeriod: (period: SummaryPeriod) => {
        // Only clear customDateRange if switching to a preset period (not custom)
        const updates: Partial<FleetState> = { selectedPeriod: period };
        if (period !== 'custom') {
          updates.customDateRange = undefined;
        }
        set(updates);
        get().loadSummary(); // Auto-refresh summary with new period
      },

      setCustomDateRange: (from: Date, to: Date) => {
        set({ selectedPeriod: 'custom', customDateRange: { from, to } });
        get().loadSummary(); // Auto-refresh summary with custom range
      },

      refreshAll: async () => {
        set({ isRefreshing: true });
        await Promise.all([get().loadSummary(), get().loadMachines(), get().loadMapPoints()]);
        set({ isRefreshing: false });
      },

      startPolling: () => {
        const { summaryPollIntervalId, statusPollIntervalId } = get();

        // Clear existing intervals
        if (summaryPollIntervalId) clearInterval(summaryPollIntervalId);
        if (statusPollIntervalId) clearInterval(statusPollIntervalId);

        // Start new polling intervals
        const newSummaryInterval = setInterval(() => {
          get().loadSummary();
        }, FLEET_SUMMARY_POLL_INTERVAL);

        const newStatusInterval = setInterval(() => {
          get().loadMachines();
        }, FLEET_STATUS_POLL_INTERVAL);

        set({
          summaryPollIntervalId: newSummaryInterval,
          statusPollIntervalId: newStatusInterval,
        });
      },

      stopPolling: () => {
        const { summaryPollIntervalId, statusPollIntervalId } = get();
        if (summaryPollIntervalId) clearInterval(summaryPollIntervalId);
        if (statusPollIntervalId) clearInterval(statusPollIntervalId);
        set({
          summaryPollIntervalId: null,
          statusPollIntervalId: null,
        });
      },

      reset: () => {
        get().stopPolling();
        set(initialState);
      },
    }),
    {
      name: 'fleet-storage',
      version: 3, // Increment to clear old cached data
      partialize: (state) => ({
        pageSize: state.pageSize,
        filters: state.filters,
        // Don't persist selectedPeriod - always start with '30days' default
      }),
    }
  )
);
